import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { LENSES, LENS_ORDER, shortLensPhrase, sourceLabel, type Persona } from "@/lib/lens-config";

const order: Persona[] = LENS_ORDER;

export default function PersonaScene() {
  const [active, setActive] = useState<Persona>("krishna");
  const lens = LENSES[active];
  const accent = `hsl(var(${lens.accentVar}))`;

  return (
    <section
      className="home-band px-5 sm:px-8"
      style={{ background: "hsl(var(--ink))" }}
    >
      <div className="max-w-6xl mx-auto w-full text-left">
        {/* Editorial header — like a chapter title page */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 mb-8 sm:mb-12">
          <p className="section-eyebrow">
            Four lenses
          </p>
          <p className="font-display italic text-manuscript/30 text-xs sm:text-sm">
            one for the decision in front of you
          </p>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-10 lg:gap-16 items-start">
          {/* LEFT — vertical ledger of names. No boxes. Just typography. */}
          <ol className="relative grid grid-cols-2 lg:block gap-x-3 gap-y-1">
            {/* hairline guide — hidden on mobile grid layout */}
            <span
              aria-hidden
              className="hidden lg:block absolute left-0 top-2 bottom-2 w-px"
              style={{ background: "hsl(var(--manuscript) / 0.08)" }}
            />
            {order.map((id, idx) => {
              const s = LENSES[id];
              const a = `hsl(var(${s.accentVar}))`;
              const isActive = id === active;
              return (
                <li key={id} className="relative">
                  <button
                    onMouseEnter={() => setActive(id)}
                    onFocus={() => setActive(id)}
                    onClick={() => setActive(id)}
                    className="group relative w-full text-left flex items-baseline gap-4 sm:gap-6 py-4 sm:py-5 pl-5 sm:pl-7 transition-colors"
                  >
                    {/* active marker — a single ink dot on the guide */}
                    <span
                      aria-hidden
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full transition-all duration-500"
                      style={{
                        width: isActive ? 9 : 4,
                        height: isActive ? 9 : 4,
                        background: isActive ? a : "hsl(var(--manuscript) / 0.2)",
                        boxShadow: isActive ? `0 0 14px ${a}` : "none",
                      }}
                    />

                    <span
                      className="font-display italic tabular-nums shrink-0 transition-colors"
                      style={{
                        fontSize: "clamp(0.85rem, 1vw, 0.95rem)",
                        color: isActive ? "hsl(var(--manuscript) / 0.65)" : "hsl(var(--manuscript) / 0.3)",
                      }}
                    >
                      {["i", "ii", "iii", "iv"][idx]}
                    </span>

                    <span className="flex-1 min-w-0">
                      <span
                        className="font-display block leading-none transition-all duration-500"
                        style={{
                          fontSize: "clamp(1.5rem, 5.5vw, 3.6rem)",
                          letterSpacing: "-0.02em",
                          color: isActive ? "hsl(var(--manuscript))" : "hsl(var(--manuscript) / 0.35)",
                          transform: isActive ? "translateX(2px)" : "translateX(0)",
                        }}
                      >
                        {s.name}
                      </span>
                      <AnimatePresence>
                        {isActive && (
                          <motion.span
                            key="meta"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="block overflow-hidden"
                          >
                            <span
                              className="block mt-2 text-[10px] uppercase tracking-[0.3em]"
                              style={{ color: a }}
                            >
                              {sourceLabel(s.id)}
                            </span>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          {/* RIGHT — the speaking voice. Large editorial quote. */}
          <div className="relative lg:pt-2">
            {/* faint seal mark — the only "ornament" */}
            <svg
              aria-hidden
              viewBox="0 0 200 200"
              className="absolute -top-6 -right-2 sm:right-0 w-28 h-28 sm:w-36 sm:h-36 opacity-[0.08] pointer-events-none"
              style={{ color: accent }}
            >
              <circle cx="100" cy="100" r="96" fill="none" stroke="currentColor" strokeWidth="0.6" />
              <circle cx="100" cy="100" r="72" fill="none" stroke="currentColor" strokeWidth="0.4" />
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * 30 * Math.PI) / 180;
                return (
                  <line
                    key={i}
                    x1={100 + 72 * Math.cos(a)}
                    y1={100 + 72 * Math.sin(a)}
                    x2={100 + 96 * Math.cos(a)}
                    y2={100 + 96 * Math.sin(a)}
                    stroke="currentColor"
                    strokeWidth="0.4"
                  />
                );
              })}
            </svg>

            <AnimatePresence mode="wait">
              <motion.div
                key={lens.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >



                {/* The quote itself — large, hanging open-quote, like an opened book */}
                <blockquote
                  className="relative mt-5 font-display text-manuscript"
                  style={{
                    fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                    lineHeight: 1.25,
                    letterSpacing: "-0.01em",
                  }}
                >
                  <span
                    aria-hidden
                    className="absolute -left-3 sm:-left-5 top-0 font-display select-none"
                    style={{
                      color: accent,
                      opacity: 0.45,
                      fontSize: "clamp(2.5rem, 5vw, 4rem)",
                      lineHeight: 1,
                      transform: "translateY(-0.15em)",
                    }}
                  >
                    “
                  </span>
                  {lens.voiceSample}
                </blockquote>

                <p
                  className="mt-6 text-manuscript/40 text-[10px] sm:text-[11px] uppercase tracking-[0.3em]"
                >
                  {lens.tonal}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* CTA — quiet, single line, editorial */}
            <div className="mt-10 flex items-center gap-5">
              <Link
                to={`/onboarding?lens=${lens.id}`}
                className="group inline-flex items-baseline gap-3 transition-colors"
                style={{ color: "hsl(var(--manuscript))" }}
              >
                <span
                  aria-hidden
                  className="block h-px transition-all duration-500"
                  style={{
                    width: 28,
                    background: accent,
                  }}
                />
                <span
                  className="font-display italic"
                  style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)" }}
                >
                  Open the {shortLensPhrase(lens.id)}
                </span>
                <span
                  className="font-display italic transition-transform duration-300 group-hover:translate-x-1"
                  style={{ color: accent, fontSize: "clamp(1rem, 1.4vw, 1.15rem)" }}
                >
                  →
                </span>
              </Link>
            </div>

            <p className="mt-3 text-manuscript/30 text-[11px]">
              Hover or tap a name to preview. Tap again to begin.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
